import { AssessmentSelectDto, AssessmentType, SurveyStatus } from '@@types/api.types';
import { ApiErrorWrapper } from '@hooks/api.hook';
import { useSurveyNavigation } from '@hooks/surveys.hook';
import { errorToast } from '@hooks/toast.hook';
import { useAssessmentsQueryOptions } from '@queries/assessment.query';
import { useGetSurvey, useGetSurveys, useInnovatorCreateSurvey } from '@queries/survey.query';
import { useGetTechnology, useGetTechnologyMrlAssessment } from '@queries/technology.query';
import { useQuery } from '@tanstack/react-query';
import { FC, useMemo, useState } from 'react';
import { AccordionTile } from '../../shared/accordion-tile.component';
import { MrlProductionRateTile } from './mrl-production-rate-tile.component';
import { MrlScoreTile } from './mrl-score-tile.component';

type IndividualMrlContentProps = {
  assessmentId: string | null;
  canEdit: boolean;
  technologyId: string;
};

export const IndividualMrlContent: FC<IndividualMrlContentProps> = (props) => {
  const { assessmentId, canEdit, technologyId } = props;

  const [isCreatingMrl, setIsCreatingMrl] = useState(false);
  const { navigateToSurvey } = useSurveyNavigation();
  const { mutateAsync: createSurvey } = useInnovatorCreateSurvey();

  const { data: technology } = useGetTechnology(technologyId);

  // Fetch all MRL assessments to find historical ones
  const assessmentQueryOptions = useAssessmentsQueryOptions({ technologyId, type: [AssessmentType.MRL] });
  const { data: allAssessments = [], isPending: isAllAssessmentsLoading } = useQuery(assessmentQueryOptions);

  // Fetch featured MRL assessment
  const { data: featuredAssessment, isPending: isFeaturedLoading } = useGetTechnologyMrlAssessment(technologyId);

  // Fetch draft MRL surveys
  const { data: draftSurveys = [] } = useGetSurveys({
    status: SurveyStatus.DRAFT,
    technologyId,
  });
  const draftMrlSurvey = draftSurveys.find((s) => s.assessmentType === AssessmentType.MRL) ?? null;

  // Determine which assessment to display
  const selectedAssessment: AssessmentSelectDto | null = useMemo(() => {
    if (!assessmentId) {
      return featuredAssessment ?? null;
    }
    return allAssessments.find((a) => a.id === assessmentId) ?? featuredAssessment ?? null;
  }, [assessmentId, allAssessments, featuredAssessment]);

  // Fetch survey for the selected assessment
  const { data: survey, isPending: isSurveyLoading } = useGetSurvey(selectedAssessment?.surveyId);

  const isLoading = isAllAssessmentsLoading || isFeaturedLoading;
  const isSurveyDataLoading = Boolean(selectedAssessment?.surveyId && isSurveyLoading);

  const mrlDescription =
    'Manufacture Readiness Level (MRL) is a measurement system used to assess the maturity of manufacturing technology, manufacturing processes, and the industrial base. The scale ranges from 1 to 10.';

  const handleStartMrl = async (): Promise<void> => {
    if (!technology?.id || isCreatingMrl) return;

    try {
      setIsCreatingMrl(true);
      const newSurvey = await createSurvey({
        assessmentType: AssessmentType.MRL,
        technologyId: technology.id,
      });
      navigateToSurvey(`/technology/${technology.id}/survey/${newSurvey.id}`);
    } catch (error) {
      const isApiError = error instanceof ApiErrorWrapper;
      const message = isApiError
        ? (error.errors?.[0]?.message ?? 'Unable to start MRL assessment.')
        : 'Unable to start MRL assessment.';
      const shouldToast = !isApiError || !error.originalError;
      console.error('Error starting MRL assessment:', error);
      if (shouldToast) {
        errorToast({ description: message });
      }
    } finally {
      setIsCreatingMrl(false);
    }
  };

  // Show only description accordion if no selected assessment
  // Empty state CTA is now in sidebar via SurveyCTA
  if (!isLoading && !selectedAssessment) {
    return (
      <AccordionTile
        content={mrlDescription}
        title="Manufacture Readiness Level"
        value="mrl-description"
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Manufacture Readiness Description */}
      <AccordionTile
        content={mrlDescription}
        title="Manufacture Readiness Level"
        value="mrl-description"
      />

      {/* Assessment Content */}
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
        {/* Left Column */}
        <div className="flex flex-col gap-4">
          <MrlScoreTile
            assessment={selectedAssessment}
            canManageAssessments={canEdit}
            draftSurvey={draftMrlSurvey}
            isCreating={isCreatingMrl}
            isLoading={isLoading || isSurveyDataLoading}
            onStartNew={canEdit ? handleStartMrl : undefined}
            survey={survey ?? null}
            technologyId={technologyId}
          />
        </div>

        {/* Right Column */}
        <div className="flex flex-col gap-4">
          <MrlProductionRateTile
            canEdit={canEdit}
            isLoading={isLoading || isSurveyDataLoading}
            survey={survey ?? null}
          />
        </div>
      </div>
    </div>
  );
};
